<template>
  <StepperModal
    ref="stepperModal"
    id="bulk-create-modal"
    :title="title ?? i18n.t('messages.bulkCreation')"
    :icon="markRaw(IconAdd)"
    :steps="steps"
    @completed="() => completed()">
    <template #upload>
      <UploadSpreadSheet
        :model-parser="modelParser"
        :allow-to-download-template="allowToDownloadTemplate"
        @worksheet-changed="worksheet = $event" />
    </template>
    <template #validation>
      <DataValidation
        :model-parser="modelParser"
        :worksheet="worksheet"
        @entities-parsed="entities = $event" />
    </template>
    <template #sync>
      <SyncProgress
        :entities="entities"
        :model-parser="modelParser"
        @sync-completed="synced = $event" />
    </template>
  </StepperModal>
</template>

<script setup lang="ts" generic="T extends IBulkCreateEntity">
  import Excel from 'exceljs';
  import { computed, markRaw, ref } from 'vue';
  import { useI18n } from 'vue-i18n';

  import { IBulkCreateEntity } from '@/common/components/controls/table/bulk-create/BulkCreateEntity';
  import { SpreadSheetModelParser } from '@/common/components/controls/table/bulk-create/model-parser/ModelParser';
  import DataValidation from '@/common/components/controls/table/bulk-create/steps/DataValidation.vue';
  import SyncProgress from '@/common/components/controls/table/bulk-create/steps/SyncProgress.vue';
  import UploadSpreadSheet from '@/common/components/controls/table/bulk-create/steps/UploadSpreadSheet.vue';
  import StepperModal, { IModalStep } from '@/common/components/templates/StepperModal.vue';
  import { IconAdd } from '@/common/icons/monochrome';

  const i18n = useI18n();

  interface IProps {
    modelParser: SpreadSheetModelParser<T>;
    allowToDownloadTemplate?: boolean;
    title?: string;
  }

  const { modelParser, allowToDownloadTemplate = true, title = null } = defineProps<IProps>();

  interface IEmits {
    (event: 'sync-completed', value: boolean): void;
  }

  const emits = defineEmits<IEmits>();

  const worksheet = ref<Excel.Worksheet>(null);
  const entities = ref<T[]>();
  const synced = ref(false);

  const stepperModal = ref<InstanceType<typeof StepperModal>>(null);

  const uploadCompleted = computed(() => worksheet.value != null);
  const validationCompleted = computed(() => uploadCompleted.value && entities.value != null);
  const syncCompleted = computed(() => validationCompleted.value);

  const steps: IModalStep[] = [
    {
      id: 'upload',
      title: i18n.t('messages.uploadSpreadsheet'),
      isCompleted: uploadCompleted,
      nextButtonCaption: i18n.t('messages.reviewData'),
    },
    {
      id: 'validation',
      title: i18n.t('messages.validateData'),
      isCompleted: validationCompleted,
      nextButtonCaption: i18n.t('messages.startSynching'),
    },
    {
      id: 'sync',
      title: i18n.t('messages.syncData'),
      isCompleted: syncCompleted,
      nextButtonCaption: i18n.t('actions.close'),
    },
  ];

  function reset() {
    worksheet.value = null;
    entities.value = null;
    synced.value = false;
  }

  function open() {
    reset();
    stepperModal.value.open();
  }

  function completed() {
    stepperModal.value.close();
    emits('sync-completed', true);
  }

  defineExpose({ open });
</script>
